<template>
    <Dialog :visible="visible" :header="isEditing ? 'Edit Grade' : 'Add New Grade'" :modal="true"
        class="w-[95vw] max-w-md" @update:visible="emit('update:visible', $event)">
        <div class="flex flex-col gap-4">
            <!-- Name -->
            <div class="flex flex-col gap-2">
                <label for="name" class="font-semibold">Name</label>
                <InputText id="name" data-testid="input-grade-name" v-model="form.name" placeholder="e.g. Excellent"
                    class="w-full"
                    :class="{ 'p-invalid': (submitted && !form.name) || getErrorMessage('name') }" />
                <small v-if="submitted && !form.name" class="text-red-500">Name is required.</small>
                <small v-if="getErrorMessage('name')" class="text-red-500">{{ getErrorMessage('name') }}</small>
            </div>

            <!-- Grade -->
            <div class="flex flex-col gap-2">
                <label for="grade" class="font-semibold">Grade</label>
                <InputText id="grade" data-testid="input-grade-label" v-model="form.grade" placeholder="e.g. A"
                    class="w-full"
                    :class="{ 'p-invalid': (submitted && !form.grade) || getErrorMessage('grade') }" />
                <small v-if="submitted && !form.grade" class="text-red-500">Grade is required.</small>
                <small v-if="getErrorMessage('grade')" class="text-red-500">{{ getErrorMessage('grade') }}</small>
            </div>

            <!-- Points (Tertiary Only) -->
            <div class="flex flex-col gap-2" v-if="isTertiary">
                <label for="point" class="font-semibold">Point <span class="text-red-500">*</span></label>
                <InputNumber id="point" data-testid="input-grade-point" v-model="form.point" :min="0" :max="50"
                    :minFractionDigits="1" :maxFractionDigits="2" placeholder="e.g. 5.0"
                    class="w-full" inputClass="w-full"
                    :class="{ 'p-invalid': (submitted && form.point === null) || getErrorMessage('point') }" />
                <small v-if="submitted && form.point === null" class="text-red-500">Required.</small>
                <small v-if="getErrorMessage('point')" class="text-red-500">{{ getErrorMessage('point') }}</small>
            </div>

            <!-- Min / Max (stacked) -->
            <div class="flex flex-col gap-2">
                <label for="minimum" class="font-semibold">Minimum Score</label>
                <InputNumber id="minimum" data-testid="input-grade-min" v-model="form.minimum" :min="0" :max="100" placeholder="0"
                    class="w-full" inputClass="w-full"
                    :class="{ 'p-invalid': (submitted && form.minimum === null) || getErrorMessage('minimum') }" />
                <small v-if="submitted && form.minimum === null" class="text-red-500">Required.</small>
                <small v-if="getErrorMessage('minimum')" class="text-red-500">{{ getErrorMessage('minimum') }}</small>
            </div>

            <div class="flex flex-col gap-2">
                <label for="maximum" class="font-semibold">Maximum Score</label>
                <InputNumber id="maximum" data-testid="input-grade-max" v-model="form.maximum" :min="0" :max="100" placeholder="100"
                    class="w-full" inputClass="w-full"
                    :class="{ 'p-invalid': (submitted && form.maximum === null) || getErrorMessage('maximum') }" />
                <small v-if="submitted && form.maximum === null" class="text-red-500">Required.</small>
                <small v-if="getErrorMessage('maximum')" class="text-red-500">{{ getErrorMessage('maximum') }}</small>
            </div>

            <!-- Group -->
            <div class="flex flex-col gap-2">
                <label for="group" class="font-semibold">Group (Optional)</label>
                <AutoComplete id="group" v-model="form.group" :suggestions="filteredGroups" @complete="searchGroup"
                    dropdown :placeholder="groupPlaceholder"
                    class="w-full" inputClass="w-full"
                    :class="{ 'p-invalid': getErrorMessage('group') }" />
                <small v-if="getErrorMessage('group')" class="text-red-500">{{ getErrorMessage('group') }}</small>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <Button size="small" label="Cancel" icon="pi pi-times" text severity="secondary"
                    @click="closeDialog" data-testid="btn-grade-form-cancel" />
                <Button size="small" label="Save" icon="pi pi-check" :loading="loading"
                    @click="saveGrade" data-testid="btn-grade-form-save" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import type { Grade, SchoolGradeInput } from '@/types/composition-functions';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';

const { isTertiary } = useSchoolFeatures();

const props = defineProps<{
    visible: boolean
    gradeToEdit?: Grade | null
    loading?: boolean
    existingGroups?: string[]
    serverErrors?: Record<string, string[]>
}>();

const getErrorMessage = (field: string) => {
  if (props.serverErrors?.[field]?.length) {
    return props.serverErrors![field][0];
  }
  return '';
};

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
    (e: 'save', grade: SchoolGradeInput): void
}>();

const isEditing = computed(() => !!props.gradeToEdit);
const submitted = ref(false);
const filteredGroups = ref<string[]>([]);
const groupPlaceholder = computed(() => isTertiary.value ? 'e.g. Undergraduate' : 'e.g. Senior Secondary');

const form = ref<SchoolGradeInput>({
  name: '',
  grade: '',
  minimum: null,
  maximum: null,
  group: '',
  point: null,
});

const searchGroup = (event: { query: string }) => {
  if (!props.existingGroups) {
    filteredGroups.value = [];
    return;
  }
  filteredGroups.value = props.existingGroups.filter(g =>
    g.toLowerCase().includes(event.query.toLowerCase()),
  );
};

watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      submitted.value = false;
      if (props.gradeToEdit) {
        form.value = {
          name: props.gradeToEdit.name,
          grade: props.gradeToEdit.grade,
          minimum: props.gradeToEdit.minimum,
          maximum: props.gradeToEdit.maximum,
          group: props.gradeToEdit.group,
          point: props.gradeToEdit.point,
        };
      } else {
        form.value = {
          name: '',
          grade: '',
          minimum: null,
          maximum: null,
          group: '',
          point: null,
        };
      }
    }
  },
);

const closeDialog = () => {
  emit('update:visible', false);
};

const saveGrade = () => {
  submitted.value = true;
  const basicValid = form.value.name && form.value.grade && form.value.minimum !== null && form.value.maximum !== null;
  if (!basicValid) return;
  if (isTertiary.value && form.value.point === null) return;
  emit('save', form.value);
};
</script>
